﻿/**
 * ChartV2 provides:
 * - getBoundingClientRect utilities
 * - scroll/resize (viewport) subscription that notifies .NET via OnViewportChanged
 */
export class ChartV2 {
    private dotnetRef: any;                 // .NET interop ref
    private rootRef: HTMLElement;           // chart root element
    private isSubscribed: boolean = false;  // subscription state
    private boundOnViewport: () => void;    // cached handler

    /**
     * @param dotNetRef Reference to the .NET object (must expose OnViewportChanged)
     * @param rootRef   Root HTML element for the chart (used for GetRootRect)
     */
    constructor(dotNetRef: any, rootRef: HTMLElement) {
        this.dotnetRef = dotNetRef;
        this.rootRef = rootRef;

        // Cache bound handler for add/removeEventListener symmetry
        this.boundOnViewport = this.OnViewportChanged.bind(this);
    }

    /**
     * Returns the viewport rect of the chart root element.
     */
    public GetRootRect(): DomRectLite {
        const r = this.rootRef.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: r.height };
    }

    /**
     * Returns the viewport rect of any given element.
     */
    public GetRect(el: Element): DomRectLite {
        const r = el.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: r.height };
    }

    /**
     * Converts SVG coordinates to screen (viewport) coordinates.
     * @param svg SVG element or its ID
     * @param x   X coordinate in SVG space
     * @param y   Y coordinate in SVG space
     * @returns   Corresponding screen coordinates
     */
    public SvgToScreen(svg: SVGSVGElement | string, x: number, y: number): { x: number; y: number } {
        const root = this.resolveSvg(svg);

        // Preferred: matrix transform via SVG APIs (robust to scaling/transforms)
        const pt = root.createSVGPoint();
        pt.x = x; pt.y = y;
        const ctm = root.getScreenCTM();
        if (ctm) {
            const sp = pt.matrixTransform(ctm);
            return { x: sp.x, y: sp.y };
        }

        // Fallback (should be rare): manual scale using viewBox vs client rect
        const r = root.getBoundingClientRect();
        const vb = root.viewBox.baseVal;
        const sx = r.width / vb.width;
        const sy = r.height / vb.height;
        return { x: r.left + x * sx, y: r.top + y * sy };
    }

    /**
     * Subscribes to viewport changes (scroll/resize, capture phase).
     */
    public Subscribe(): void {
        if (this.isSubscribed === true) {
            return;
        }
        window.addEventListener('scroll', this.boundOnViewport, true);
        window.addEventListener('resize', this.boundOnViewport, true);
        this.isSubscribed = true;
    }

    /**
     * Unsubscribes from viewport changes.
     */
    public Unsubscribe(): void {
        if (this.isSubscribed === false) {
            return;
        }
        window.removeEventListener('scroll', this.boundOnViewport, true);
        window.removeEventListener('resize', this.boundOnViewport, true);
        this.isSubscribed = false;
    }

    /**
     * Disposes the instance and removes listeners.
     */
    public Dispose(): void {
        this.Unsubscribe();
    }

    /**
     * Invoked on any viewport change; notifies .NET.
     */
    private OnViewportChanged(): void {
        if (this.dotnetRef) {
            // .NET method name must match your component’s [JSInvokable] method
            this.dotnetRef.invokeMethodAsync('OnViewportChanged');
        }
    }

    /**
     * Resolves an SVG element from an element or its ID.
     * @param svg SVG element or its ID
     * @returns   The resolved SVGSVGElement
     * @throws    If the element is not found or not an SVGSVGElement
     */
    private resolveSvg(svg: SVGSVGElement | string): SVGSVGElement {
        if (typeof svg !== 'string') {
            if (svg instanceof SVGSVGElement) {
                return svg;
            }
            throw new Error('ChartV2: parameter is not an <svg> element.');
        }

        const el = document.getElementById(svg);
        if (el instanceof SVGSVGElement) {
            return el;
        }
        if (el !== null) {
            // If you accidentally passed the wrapper <div> id, you’ll land here.
            throw new Error(`ChartV2: element with id "${svg}" is not <svg>.`);
        }
        throw new Error(`ChartV2: element not found for id "${svg}".`);
    }
}

/**
 * Lightweight DOMRect-like structure.
 */
export interface DomRectLite {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * Initializes a new Chart instance.
 *
 * @param dotNetRef - Reference to the .NET object
 * @param elementRef - Root HTML element of the chart
 * @returns The Chart instance
 */
window['InitChartV2'] = function (dotNetRef: any, elementRef: HTMLElement) {
    return new ChartV2(dotNetRef, elementRef);
};